// The shell (02-DESIGN/03-console/01-the-console.md § the shape, § the look):
// the rail and a single content column of at most 1040 px, no top bar. The
// routes are the pages of § the address; the former `provider` and `sign-ins`
// go to their new addresses. A page the role is not offered says *Not
// allowed* instead of rendering a form whose verb the broker would drop — the
// rail never listed it, and the console renders nothing as a permission. Every
// page stands alone (§ conventions: a reply it cannot draw): one that cannot
// draw a reply says so in the content column, and the rail stays.

import { useMemo, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { PAGE_TITLES, tenantPath } from "../address/address.ts";
import type { Who } from "../platform/bus.ts";
import { workspaceName } from "../platform/names.ts";
import type { TenantProfile } from "../platform/profile.ts";
import { pageOffered, type RailEntry } from "../roles/offers.ts";
import { ApprovalPage } from "../surfaces/approvals/ApprovalPage.tsx";
import { ApprovalsPage } from "../surfaces/approvals/ApprovalsPage.tsx";
import { AppPage } from "../surfaces/apps/AppPage.tsx";
import { AppsPage } from "../surfaces/apps/AppsPage.tsx";
import { AuditPage, AuditRecordPage } from "../surfaces/audit/AuditPage.tsx";
import { ConfigurationPage } from "../surfaces/configuration/ConfigurationPage.tsx";
import { DocumentPage } from "../surfaces/configuration/DocumentPage.tsx";
import { GuardrailPage } from "../surfaces/guardrails/GuardrailPage.tsx";
import { GuardrailsPage } from "../surfaces/guardrails/GuardrailsPage.tsx";
import { RequiredGuardrailPage } from "../surfaces/guardrails/RequiredGuardrailPage.tsx";
import { HomePage } from "../surfaces/home/HomePage.tsx";
import { ActivityPage } from "../surfaces/me/ActivityPage.tsx";
import { CredentialsPage } from "../surfaces/me/CredentialsPage.tsx";
import { RecordPage } from "../surfaces/me/RecordPage.tsx";
import { ModelPage } from "../surfaces/models/ModelPage.tsx";
import { ModelsPage } from "../surfaces/models/ModelsPage.tsx";
import { MemberPage } from "../surfaces/people/MemberPage.tsx";
import { PeoplePage } from "../surfaces/people/PeoplePage.tsx";
import { SecretsPage } from "../surfaces/secrets/SecretsPage.tsx";
import { ReceiptPage } from "../surfaces/settings/ReceiptPage.tsx";
import { SettingsPage } from "../surfaces/settings/SettingsPage.tsx";
import { BlueprintRevisionPage } from "../surfaces/workspaces/BlueprintRevisionPage.tsx";
import { ChildPage } from "../surfaces/workspaces/ChildPage.tsx";
import { NamedBlueprintPage } from "../surfaces/workspaces/NamedBlueprintPage.tsx";
import { WorkspacesPage } from "../surfaces/workspaces/WorkspacesPage.tsx";
import { PanelPage, PanelRecordPage } from "../surfaces/solutions/PanelPage.tsx";
import { RolePage } from "../surfaces/solutions/RolePage.tsx";
import { SolutionPage } from "../surfaces/solutions/SolutionPage.tsx";
import { SolutionsPage } from "../surfaces/solutions/SolutionsPage.tsx";
import { ToolkitPage } from "../surfaces/toolkits/ToolkitPage.tsx";
import { ToolkitsPage } from "../surfaces/toolkits/ToolkitsPage.tsx";
import { IndexPage } from "../surfaces/stores/IndexPage.tsx";
import { ItemPage } from "../surfaces/stores/ItemPage.tsx";
import { ToolPage } from "../surfaces/toolkits/ToolPage.tsx";
import { StorePage } from "../surfaces/stores/StorePage.tsx";
import { StoresPage } from "../surfaces/stores/StoresPage.tsx";
import { TypePage } from "../surfaces/stores/TypePage.tsx";
import { WorkloadPage } from "../surfaces/workloads/WorkloadPage.tsx";
import { WorkloadsPage } from "../surfaces/workloads/WorkloadsPage.tsx";
import { WorkspaceProvider, type Workspace } from "../surfaces/surface.tsx";
import { Boundary } from "../ui/boundary.tsx";
import { PageHead } from "../ui/components.tsx";
import { FoldBar, useFold } from "./Fold.tsx";
import { Rail } from "./Rail.tsx";

export function Shell({ tenant, who, signOut }: { tenant: TenantProfile; who: Who; signOut: () => void }) {
  const role = who.role;
  const slug = tenant.slug;
  // The workspace's name: the profile's when the console opened, and what
  // Settings set since (01-tenants-and-roles.md § the account) — kept with
  // the slug it was set for, so another workspace's profile is never shown
  // under a stale rename.
  const [renamed, setRenamed] = useState<{ slug: string; name: string } | undefined>(undefined);
  const name =
    renamed !== undefined && renamed.slug === slug ? renamed.name : workspaceName(tenant.slug, tenant.name);
  const workspace = useMemo<Workspace>(
    () => ({
      name,
      rename: (n) => {
        setRenamed({ slug, name: workspaceName(slug, n) });
      },
    }),
    [name, slug],
  );
  const { pathname } = useLocation();
  // At a phone's width the rail folds behind the bar's *Menu* and opens in
  // place of the page (§ the look: at a phone's width; hq issue 220).
  const fold = useFold();
  const gate = (entry: RailEntry, page: ReactNode) =>
    pageOffered(role, entry) ? (
      <Boundary part="page" at={pathname}>
        {page}
      </Boundary>
    ) : (
      <NotOffered name={name} entry={entry} />
    );
  return (
    <WorkspaceProvider value={workspace}>
      <div className={fold.open ? "console rail-open" : "console"}>
        <FoldBar name={name} fold={fold} />
        <Rail
          slug={slug}
          name={name}
          who={who}
          signOut={signOut}
          folded={fold.open}
          onLeave={fold.open ? fold.close : undefined}
        />
        <main className="main">
          <div className="page">
            <Routes>
              <Route index element={gate("home", <HomePage tenant={tenant} />)} />
              <Route path="approvals" element={gate("approvals", <ApprovalsPage />)} />
              <Route path="approvals/:id" element={gate("approvals", <ApprovalPage />)} />
              <Route path="apps" element={gate("apps", <AppsPage />)} />
              <Route path="apps/:id" element={gate("apps", <AppPage />)} />
              <Route path="toolkits" element={gate("toolkits", <ToolkitsPage />)} />
              <Route path="toolkits/:name" element={gate("toolkits", <ToolkitPage />)} />
              <Route path="toolkits/:name/tools/:tool" element={gate("toolkits", <ToolPage />)} />
              <Route path="workloads" element={gate("workloads", <WorkloadsPage />)} />
              <Route path="workloads/:id" element={gate("workloads", <WorkloadPage />)} />
              <Route path="solutions" element={gate("solutions", <SolutionsPage />)} />
              <Route path="solutions/:name" element={gate("solutions", <SolutionPage />)} />
              <Route path="solutions/:name/:panel" element={gate("solutions", <PanelPage />)} />
              <Route path="solutions/:name/roles/:role" element={gate("solutions", <RolePage />)} />
              <Route path="solutions/:name/:panel/:key" element={gate("solutions", <PanelRecordPage />)} />
              <Route path="people" element={gate("people", <PeoplePage tenant={tenant} />)} />
              <Route
                path="people/:provider/:claim/:value"
                element={gate("people", <MemberPage tenant={tenant} />)}
              />
              <Route path="workspaces" element={gate("workspaces", <WorkspacesPage />)} />
              <Route path="workspaces/:child" element={gate("workspaces", <ChildPage />)} />
              <Route
                path="workspaces/blueprints/:name"
                element={gate("workspaces", <NamedBlueprintPage />)}
              />
              <Route
                path="workspaces/blueprints/:name/history/:revision"
                element={gate("workspaces", <BlueprintRevisionPage />)}
              />
              <Route path="models" element={gate("models", <ModelsPage />)} />
              <Route path="models/:id" element={gate("models", <ModelPage />)} />
              <Route path="configuration" element={gate("configuration", <ConfigurationPage />)} />
              <Route
                path="configuration/:workload/:document"
                element={gate("configuration", <DocumentPage />)}
              />
              <Route path="guardrails" element={gate("guardrails", <GuardrailsPage />)} />
              <Route path="guardrails/:id" element={gate("guardrails", <GuardrailPage />)} />
              <Route path="guardrails/required/:id" element={gate("guardrails", <RequiredGuardrailPage />)} />
              <Route path="stores" element={gate("stores", <StoresPage />)} />
              <Route path="stores/:store" element={gate("stores", <StorePage />)} />
              <Route path="stores/:store/types/:type" element={gate("stores", <TypePage />)} />
              <Route path="stores/:store/indexes/:index" element={gate("stores", <IndexPage />)} />
              <Route path="stores/:store/items/*" element={gate("stores", <ItemPage />)} />
              <Route path="secrets" element={gate("secrets", <SecretsPage />)} />
              <Route path="audit" element={gate("audit", <AuditPage />)} />
              <Route path="audit/:panel/:key" element={gate("audit", <AuditRecordPage />)} />
              <Route path="settings" element={gate("settings", <SettingsPage tenant={tenant} />)} />
              <Route
                path="settings/billing"
                element={<Navigate to={tenantPath(slug, "settings")} state={{ tab: "billing" }} replace />}
              />
              <Route path="settings/billing/receipts/:id" element={gate("settings", <ReceiptPage />)} />
              <Route path="me/activity" element={gate("me/activity", <ActivityPage />)} />
              <Route path="me/activity/:id" element={gate("me/activity", <RecordPage />)} />
              <Route path="me/credentials" element={gate("me/credentials", <CredentialsPage />)} />
              <Route
                path="provider"
                element={<Navigate to={tenantPath(slug, "settings")} state={{ tab: "sign-in" }} replace />}
              />
              <Route path="sign-ins" element={<Navigate to={tenantPath(slug, "me/activity")} replace />} />
              <Route path="*" element={<Navigate to={tenantPath(slug)} replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </WorkspaceProvider>
  );
}

function NotOffered({ name, entry }: { name: string; entry: RailEntry }) {
  const title = entry === "home" ? "Home" : PAGE_TITLES[entry];
  return <PageHead title="Not allowed" line={`${title} is not offered to your role in ${name}.`} />;
}
